// ── Primitive value arrays (single source of truth) ───────────────────────

export const REQUIREMENT_LEVELS = ['MANDATORY', 'OPTIONAL', 'DESIRABLE'];

export const TASK_TYPES = [
    'APPLICATION_FORM_DATA_CONFIGURABLE',
    'UPLOAD_DOCUMENTS_CONFIGURABLE',
    'PLAID_DATA_CONFIGURABLE',
];

export const SECTION_TYPES = ['OWNER', 'BUSINESS', 'FILES', 'BANK_ACCESS'];

// Explanatory text shown below each configuration field in the UI
export const CONFIG_DESCRIPTIONS = {
    // Field configs
    readOnly:               'Prevents the user from editing this field. Combine with defaultValue to pre-fill fixed data.',
    helperText:             'Hint text shown below the field to guide the user.',
    fieldRows:              'Number of visible text rows. Use values > 1 to make the field a multiline textarea.',
    defaultValue:           'Pre-filled value. Only takes effect when readOnly is enabled.',
    selectOption:           'Comma-separated list of options shown in the dropdown (e.g. Option A, Option B).',
    uiStaticOptionsMapName: 'Server-side options map to populate the dropdown dynamically.',
    signatureText:          'Main text displayed above the signature pad.',
    authorizationText:      'Authorization/consent text displayed below the signature pad.',
    acceptedFileTypes:      'Allowed file types as extensions (.pdf, .xlsx) or MIME types (image/png). Comma-separated.',
    maxNumberOfFiles:       'Maximum number of files the user can upload. Default: 10.',
    // Section configs
    sectionType:            'Defines the data domain of this section. Controls which field names and UI components are allowed.',
    maxOwners:              'Maximum number of owner entries allowed in this section (1–10). Only valid for OWNER sections.',
    hideSectionInUi:        'When true, this section is hidden from the client-facing form but its data is still processed.',
    industrySpecific:       'When true, this section is only shown for specific industry types.',
    activationFlagName:     'Name of the field whose value controls whether this section is shown.',
    activationFlagValue:    'The value that activationFlagName must have for this section to be visible.',
};

// UI component type groups
const UI_TEXT_TYPES = [
    'INPUT_FIELD_TEXT', 'INPUT_FIELD_DATE', 'INPUT_FIELD_EMAIL',
    'INPUT_FIELD_PHONE', 'INPUT_FIELD_NUMBER', 'INPUT_FIELD_PASSWORD',
    'INPUT_FIELD_URL', 'INPUT_FIELD_ADDRESS',
];
export const SELECT_TYPES     = ['INPUT_FIELD_SELECT', 'INPUT_FIELD_SELECT_CUSTOM', 'INPUT_FIELD_SELECT_YES_NO'];
export const STATIC_OPTIONS_MAP_NAMES = ['countryOptions', 'industryOptions', 'businessTypeOptions', 'stateOptions'];
export const FILE_UPLOAD_TYPES = ['FILE_UPLOAD_FIELD', 'FILE_UPLOAD_MULTIPLE'];

export const UI_COMPONENT_TYPES = [
    ...UI_TEXT_TYPES,
    ...SELECT_TYPES,
    'CHECKBOX', 'RADIO_GROUP', 'TEXTAREA',
    ...FILE_UPLOAD_TYPES,
    'SIGNATURE',
];

// ── Internal field type lists ──────────────────────────────────────────────

const OWNER_INTERNAL_TYPES = [
    'OWNER_ID', 'OWNER_FIRST_NAME', 'OWNER_LAST_NAME', 'OWNER_EMAIL',
    'OWNER_PHONE', 'OWNER_DATE_OF_BIRTH', 'OWNER_SOCIAL_SECURITY_NUMBER',
    'OWNER_ADDRESS', 'OWNER_TITLE', 'OWNER_PERCENTAGE_OWNERSHIP',
    'OWNER_SIGNATURE', 'OTHER_OWNER',
];
const BUSINESS_INTERNAL_TYPES = [
    'BUSINESS_NAME', 'DBA', 'FEDERAL_TAX_ID', 'REFERRED_BY', 'REFERRED_BY_EMAIL',
    'CURRENTLY_FACTORED_BY', 'OPEN_AR', 'BUSINESS_PHONE', 'BUSINESS_EMAIL',
    'BUSINESS_START_DATE', 'BUSINESS_ADDRESS', 'MC_NUMBER', 'DOT_NUMBER',
    'INDUSTRY_TYPE', 'BUSINESS_TYPE', 'BUSINESS_WEBSITE', 'OTHER_BUSINESS',
];
const CONTACT_INTERNAL_TYPES  = ['CONTACT_NAME', 'CONTACT_EMAIL', 'CONTACT_PHONE'];
const DEBTOR_INTERNAL_TYPES   = [
    'DEBTOR_BUSINESS_NAME', 'DEBTOR_EIN', 'DEBTOR_BUSINESS_ADDRESS',
    'DEBTOR_CONTACT_NAME', 'DEBTOR_CONTACT_PHONE', 'DEBTOR_CONTACT_EMAIL',
    'OTHER_DEBTOR_BUSINESS',
];
const COMPLIANCE_INTERNAL_TYPES = [
    'CURRENT_ON_TAXES', 'CURRENT_ON_TAXES_EXPLANATION',
    'CURRENT_ON_TAX_RETURNS', 'CURRENT_ON_TAX_RETURNS_EXPLANATION',
    'PRESENTLY_BEING_SUED', 'PRESENTLY_BEING_SUED_EXPLANATION',
    'SUBJECT_TO_JUDGMENTS', 'SUBJECT_TO_JUDGMENTS_EXPLANATION',
];
const FILES_INTERNAL_TYPES = [
    'UCC_DOCUMENT', 'COBALT_SOS_SCREENSHOT', 'UCC_ROX_REPORT', 'FMCSA_ROX_REPORT',
    'UCC_FILING_DETAILS_ROX_REPORT', 'UCC_FILING_IMAGE', 'UCC_FILING_E_FILE',
    'LEXIS_PDF_OWNER', 'LEXIS_PDF_BUSINESS', 'LEXIS_PDF_INSTANT_ID',
    'USER_FILE', 'GMAIL_FILE', 'TO_SUBMIT_UCC_FILE', 'CREDIT_MEMO', 'DOCUSIGN_PDF',
    'INVOICE', 'SIGNATURE', 'AR_AGING', 'BALANCE_SHEET', 'P_AND_L_STATEMENT', 'CASH_FLOW_STATEMENT',
    'DRIVER_LICENSE_ID', 'VOIDED_CHECK', 'ADDITIONAL_DOCUMENTS',
    'CLIENT_SUPPLIERS_AGREEMENTS', 'CLIENT_PORTAL_FILES', 'AGREEMENT', 'BANK_STATEMENT',
];
// File types the client portal can receive (first one is the default)
export const CLIENT_PORTAL_FILE_INTERNAL_TYPES = ['CLIENT_PORTAL_FILES', 'AR_AGING', 'BANK_STATEMENT', 'BALANCE_SHEET', 'P_AND_L_STATEMENT', 'CASH_FLOW_STATEMENT'];

export const INTERNAL_FIELD_TYPES_BY_SECTION = {
    OWNER:       [{ group: 'Owner',               values: OWNER_INTERNAL_TYPES }],
    BUSINESS:    [
        { group: 'Business Information',           values: BUSINESS_INTERNAL_TYPES },
        { group: 'Contact',                        values: CONTACT_INTERNAL_TYPES },
        { group: 'Debtor',                         values: DEBTOR_INTERNAL_TYPES },
        { group: 'Compliance',                     values: COMPLIANCE_INTERNAL_TYPES },
    ],
    FILES:       [{ group: 'Documents',            values: FILES_INTERNAL_TYPES }],
    BANK_ACCESS: [],
};

export const INTERNAL_FIELD_TYPES_GROUPED = Object.values(INTERNAL_FIELD_TYPES_BY_SECTION).flat();
export const INTERNAL_FIELD_TYPES = INTERNAL_FIELD_TYPES_GROUPED.flatMap(g => g.values);

// ── InternalFieldType → DTO field name ────────────────────────────────────

// Free types: no fixed field name (OTHER_* = custom, ADDRESS = composite field)
export const FREE_INTERNAL_TYPES = [
    'OTHER_OWNER', 'OTHER_BUSINESS', 'OTHER_DEBTOR_BUSINESS',
    'BUSINESS_ADDRESS', 'DEBTOR_BUSINESS_ADDRESS', 'OWNER_ADDRESS',
];

export const INTERNAL_FIELD_TYPE_TO_LABEL = {
    BUSINESS_NAME:                'Business Name',
    DBA:                          'DBA',
    FEDERAL_TAX_ID:               'Federal Tax ID',
    REFERRED_BY:                  'Referred By',
    REFERRED_BY_EMAIL:            'Referred By Email',
    CURRENTLY_FACTORED_BY:        'Currently Factored By',
    OPEN_AR:                      'Open A/R',
    BUSINESS_PHONE:               'Business Phone',
    BUSINESS_EMAIL:               'Business Email',
    BUSINESS_START_DATE:          'Business Start Date',
    BUSINESS_ADDRESS:             'Business Address',
    MC_NUMBER:                    'MC Number',
    DOT_NUMBER:                   'DOT Number',
    INDUSTRY_TYPE:                'Industry Type',
    BUSINESS_TYPE:                'Business Type',
    BUSINESS_WEBSITE:             'Business Website',
    CONTACT_NAME:                 'Contact Name',
    CONTACT_EMAIL:                'Contact Email',
    CONTACT_PHONE:                'Contact Phone',
    OTHER_BUSINESS:               '',
    DEBTOR_BUSINESS_NAME:         'Debtor Business Name',
    DEBTOR_EIN:                   'Debtor EIN',
    DEBTOR_BUSINESS_ADDRESS:      'Debtor Business Address',
    DEBTOR_CONTACT_NAME:          'Debtor Contact Name',
    DEBTOR_CONTACT_PHONE:         'Debtor Contact Phone',
    DEBTOR_CONTACT_EMAIL:         'Debtor Contact Email',
    OTHER_DEBTOR_BUSINESS:        '',
    OWNER_ID:                     'Owner ID',
    OWNER_FIRST_NAME:             'First Name',
    OWNER_LAST_NAME:              'Last Name',
    OWNER_EMAIL:                  'Email',
    OWNER_PHONE:                  'Phone',
    OWNER_DATE_OF_BIRTH:          'Date of Birth',
    OWNER_SOCIAL_SECURITY_NUMBER: 'Social Security Number',
    OWNER_ADDRESS:                'Address',
    OWNER_TITLE:                  'Title',
    OWNER_PERCENTAGE_OWNERSHIP:   'Ownership %',
    OWNER_SIGNATURE:              'Signature',
    OTHER_OWNER:                  '',
    CURRENT_ON_TAXES:             'Current on Taxes',
    CURRENT_ON_TAXES_EXPLANATION: 'Current on Taxes Explanation',
    CURRENT_ON_TAX_RETURNS:       'Current on Tax Returns',
    CURRENT_ON_TAX_RETURNS_EXPLANATION: 'Current on Tax Returns Explanation',
    PRESENTLY_BEING_SUED:         'Presently Being Sued',
    PRESENTLY_BEING_SUED_EXPLANATION: 'Presently Being Sued Explanation',
    SUBJECT_TO_JUDGMENTS:         'Subject to Judgments',
    SUBJECT_TO_JUDGMENTS_EXPLANATION: 'Subject to Judgments Explanation',
    AR_AGING:                     'AR Aging Report',
    BANK_STATEMENT:               'Bank Statement',
    BALANCE_SHEET:                'Balance Sheet',
    P_AND_L_STATEMENT:            'Income Statement (P&L)',
    CASH_FLOW_STATEMENT:          'Cash Flow Statement',
};

export const INTERNAL_FIELD_TYPE_TO_FIELD_NAME = {
    BUSINESS_NAME:                'businessName',
    DBA:                          'businessDbaName',
    FEDERAL_TAX_ID:               'federalTaxId',
    REFERRED_BY:                  'brokerName',
    REFERRED_BY_EMAIL:            'brokerEmail',
    CURRENTLY_FACTORED_BY:        'factorName',
    OPEN_AR:                      'openAr',
    BUSINESS_PHONE:               'businessPhone',
    BUSINESS_EMAIL:               'businessEmail',
    BUSINESS_START_DATE:          'businessStartDate',
    BUSINESS_ADDRESS:             'businessAddress1',
    MC_NUMBER:                    'mcNumber',
    DOT_NUMBER:                   'dotNumber',
    INDUSTRY_TYPE:                'industryType',
    BUSINESS_TYPE:                'businessType',
    BUSINESS_WEBSITE:             'businessWebsite',
    CONTACT_NAME:                 'contactName',
    CONTACT_EMAIL:                'contactEmail',
    CONTACT_PHONE:                'contactPhone',
    DEBTOR_BUSINESS_NAME:         'debtorBusinessName',
    DEBTOR_EIN:                   'debtorEin',
    DEBTOR_BUSINESS_ADDRESS:      'debtorAddress1',
    DEBTOR_CONTACT_NAME:          'debtorContactName',
    DEBTOR_CONTACT_PHONE:         'debtorContactPhone',
    DEBTOR_CONTACT_EMAIL:         'debtorContactEmail',
    OWNER_FIRST_NAME:             'firstName',
    OWNER_LAST_NAME:              'lastName',
    OWNER_EMAIL:                  'email',
    OWNER_PHONE:                  'phone',
    OWNER_DATE_OF_BIRTH:          'dateOfBirth',
    OWNER_SOCIAL_SECURITY_NUMBER: 'socialSecurityNumber',
    OWNER_ADDRESS:                'address1',
    OWNER_TITLE:                  'title',
    OWNER_PERCENTAGE_OWNERSHIP:   'percentageOwnership',
    OWNER_SIGNATURE:              'signatureBase64',
};

// ── Configuration rules ────────────────────────────────────────────────────
// level: 'error' = blocks save | 'warning' = advisory, allows save

const rule = (level, message, values) => ({ values, level, message });

const selectRequiredConfig  = rule('error',   'SELECT requires selectOption or uiStaticOptionsMapName', ['selectOption|uiStaticOptionsMapName']);
const optionsRequiredConfig = (type) => rule('error', `${type} requires selectOption`, ['selectOption']);
const fileUploadSectionRule = rule('warning', 'File upload components are typically used in FILES sections', ['FILES']);

export const PORTAL_CONFIG_RULES = {
    taskTypeRules: {
        APPLICATION_FORM_DATA_CONFIGURABLE: {
            allowedSectionTypes: rule('error',   'Only OWNER and BUSINESS sections are allowed in application form tasks', ['OWNER', 'BUSINESS']),
        },
        UPLOAD_DOCUMENTS_CONFIGURABLE: {
            allowedSectionTypes:        rule('error', 'Upload tasks only support FILES sections', ['FILES']),
            requiredFieldInternalType:  rule('error', `Fields in upload tasks must use one of these internal types: ${CLIENT_PORTAL_FILE_INTERNAL_TYPES.join(', ')}`, CLIENT_PORTAL_FILE_INTERNAL_TYPES),
            requiredFieldUiComponent:   rule('error', 'Fields in upload tasks must use FILE_UPLOAD_FIELD component type', ['FILE_UPLOAD_FIELD']),
        },
        PLAID_DATA_CONFIGURABLE: {
            allowedSectionTypes: rule('warning', 'Plaid tasks are expected to use BANK_ACCESS sections', ['BANK_ACCESS']),
        },
    },

    sectionTypeRules: {
        OWNER: {
            allowedUiComponents: rule('error', 'This component type is not allowed in OWNER sections', [
                ...UI_TEXT_TYPES.filter(t => t !== 'INPUT_FIELD_URL'),
                ...SELECT_TYPES,
                'CHECKBOX', 'SIGNATURE',
            ]),
            allowedInternalTypes: rule('error', 'Only OWNER internal field types are allowed in OWNER sections', OWNER_INTERNAL_TYPES),
            requireInternalType: true,
            fieldNames: [
                {
                    group: 'Owner',
                    values: [
                        'firstName', 'lastName', 'title', 'email', 'phone',
                        'percentageOwnership', 'dateOfBirth', 'socialSecurityNumber',
                        'address1', 'address2', 'city', 'state', 'zip5', 'countryCode',
                        'signatureBase64',
                    ],
                },
            ],
        },
        BUSINESS: {
            allowedUiComponents: rule('error', 'This component type is not allowed in BUSINESS sections', [
                ...UI_TEXT_TYPES,
                ...SELECT_TYPES,
                'CHECKBOX', 'RADIO_GROUP', 'TEXTAREA',
            ]),
            allowedInternalTypes: rule('error', 'OWNER and file internal field types are not allowed in BUSINESS sections', [
                ...BUSINESS_INTERNAL_TYPES,
                ...CONTACT_INTERNAL_TYPES,
                ...DEBTOR_INTERNAL_TYPES,
                ...COMPLIANCE_INTERNAL_TYPES,
            ]),
            fieldNames: [
                {
                    group: 'Business',
                    values: [
                        'businessName', 'businessDbaName', 'businessDescription',
                        'businessEmail', 'businessPhone', 'businessStartDate',
                        'businessWebsite', 'federalTaxId', 'industryType', 'businessType',
                        'mcNumber', 'dotNumber', 'openAr',
                        'brokerName', 'brokerEmail', 'factorName',
                        'businessAddress1', 'businessAddress2', 'businessCity',
                        'businessState', 'businessZip5', 'businessCountryCode',
                    ],
                },
                {
                    group: 'Contact',
                    values: ['contactName', 'contactPhone', 'contactEmail'],
                },
                {
                    group: 'Debtor',
                    values: [
                        'debtorBusinessName', 'debtorDbaName', 'debtorEin',
                        'debtorPhone', 'debtorEmail', 'debtorWebsite',
                        'debtorBusinessType', 'debtorIndustryType',
                        'debtorMcNumber', 'debtorDotNumber',
                        'debtorAddress1', 'debtorAddress2', 'debtorCity',
                        'debtorState', 'debtorZip5', 'debtorCountryCode',
                        'debtorContactName', 'debtorContactPhone', 'debtorContactEmail',
                    ],
                },
            ],
        },
        FILES: {
            allowedUiComponents: rule('error', 'FILES sections only support file upload components', FILE_UPLOAD_TYPES),
        },
        BANK_ACCESS: {
            allowedUiComponents: rule('warning', 'Bank access sections are managed by Plaid — custom fields may be ignored', []),
        },
    },

    uiComponentRules: {
        CHECKBOX:                  { requiredConfigs: optionsRequiredConfig('CHECKBOX') },
        RADIO_GROUP:               { requiredConfigs: optionsRequiredConfig('RADIO_GROUP') },
        INPUT_FIELD_SELECT: {
            requiredConfigs: rule('error', 'INPUT_FIELD_SELECT requires uiStaticOptionsMapName', ['uiStaticOptionsMapName']),
            configValues: {
                uiStaticOptionsMapName: rule('error', `Must be one of: ${STATIC_OPTIONS_MAP_NAMES.join(', ')}`, STATIC_OPTIONS_MAP_NAMES),
            },
        },
        INPUT_FIELD_SELECT_CUSTOM: { requiredConfigs: selectRequiredConfig },
        INPUT_FIELD_SELECT_YES_NO: {
            // No options config needed — Yes/No values are fixed in the component
        },
        SIGNATURE: {
            recommendedInternalTypes: rule('warning', 'SIGNATURE is typically paired with OWNER_SIGNATURE or SIGNATURE internal type', ['OWNER_SIGNATURE', 'SIGNATURE']),
        },
        ...Object.fromEntries(
            FILE_UPLOAD_TYPES.map(t => [t, { recommendedSectionTypes: fileUploadSectionRule }])
        ),
    },
};
